import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import StatusBadge from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { generateQuotePdf } from "../lib/pdf";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Plus, FileText, Download, TrendingUp, Eye, Pencil, Trash2 } from "lucide-react";

const fmt = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function CustomerDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    api.get("/quotes").then(({ data }) => setQuotes(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const confirmDelete = async () => {
    const id = deleteTarget.id;
    setDeleteTarget(null);
    try {
      await api.delete(`/quotes/${id}`);
      setQuotes((qs) => qs.filter((q) => q.id !== id));
      toast.success("Quote deleted");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete");
    }
  };

  const pipeline = quotes.reduce((s, q) => s + (q.one_time_total || 0), 0);
  const active = quotes.filter((q) => ["draft", "sent"].includes(q.status)).length;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="customer-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-primary">Welcome, {user?.name?.split(" ")[0]}</span>
          <h1 className="mt-2 font-display font-black text-4xl tracking-tighter">Your Quotes</h1>
        </div>
        <Button onClick={() => navigate("/build")} data-testid="new-quote-btn" className="rounded-none bg-primary hover:bg-[#5E1F92] text-white font-mono uppercase tracking-widest">
          <Plus className="h-4 w-4 mr-1" /> New Quote
        </Button>
      </div>

      <div className="grid sm:grid-cols-3 gap-px bg-zinc-200 border border-zinc-200 mb-10">
        {[
          { label: "Total Quotes", value: quotes.length, icon: FileText },
          { label: "Active", value: active, icon: TrendingUp },
          { label: "Total Value", value: fmt(pipeline), icon: TrendingUp },
        ].map((s) => (
          <div key={s.label} className="bg-white p-6">
            <p className="font-mono text-xs uppercase tracking-widest text-zinc-500">{s.label}</p>
            <p className="mt-2 font-display font-black text-3xl tracking-tighter">{s.value}</p>
          </div>
        ))}
      </div>

      {loading ? (
        <p className="font-mono text-sm text-zinc-500">Loading…</p>
      ) : quotes.length === 0 ? (
        <div className="border border-dashed border-zinc-300 bg-white p-16 text-center">
          <FileText className="h-10 w-10 text-zinc-300 mx-auto" />
          <p className="mt-4 text-zinc-600">No quotes yet.</p>
          <Button onClick={() => navigate("/build")} data-testid="empty-build-btn" className="mt-4 rounded-none bg-primary hover:bg-[#5E1F92] text-white font-mono uppercase tracking-widest">
            Build your first quote
          </Button>
        </div>
      ) : (
        <div className="border border-zinc-200 bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-200 font-mono text-xs uppercase tracking-widest text-zinc-500">
                <th className="text-left px-4 py-3">Quote</th>
                <th className="text-left px-4 py-3">Created</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">One-time</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {quotes.map((q) => (
                <tr key={q.id} data-testid={`quote-row-${q.id}`} className="border-b border-zinc-100 hover:bg-zinc-50">
                  <td className="px-4 py-3">
                    <Link to={`/quotes/${q.id}`} className="font-medium hover:text-primary">
                      #{q.id.slice(-8).toUpperCase()}
                    </Link>
                    <p className="text-xs text-zinc-500">{q.client_name}</p>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-zinc-500">{new Date(q.created_at).toLocaleDateString()}</td>
                  <td className="px-4 py-3"><StatusBadge status={q.status} /></td>
                  <td className="px-4 py-3 text-right font-mono">{fmt(q.one_time_total)}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-1 flex-wrap">
                      <Button size="sm" variant="ghost" data-testid={`view-${q.id}`} onClick={() => navigate(`/quotes/${q.id}`)} className="rounded-none h-8 px-2" title="View">
                        <Eye className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" data-testid={`edit-${q.id}`} disabled={q.deposit_paid || q.signed} onClick={() => navigate(`/build?edit=${q.id}`)} className="rounded-none h-8 px-2 disabled:opacity-40" title={(q.deposit_paid || q.signed) ? "Locked — signed or paid" : "Edit"}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" data-testid={`delete-${q.id}`} disabled={q.deposit_paid || q.signed} onClick={() => setDeleteTarget(q)} className="rounded-none h-8 px-2 text-red-600 hover:text-red-700 hover:bg-red-50 disabled:opacity-40 disabled:text-zinc-400" title={(q.deposit_paid || q.signed) ? "Locked — signed or paid" : "Delete"}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="sm" variant="outline" data-testid={`download-pdf-${q.id}`} onClick={() => generateQuotePdf(q)} className="rounded-none h-8 border-zinc-300 px-2" title="Download PDF">
                        <Download className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this quote?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget && `Quote #${deleteTarget.id.slice(-8).toUpperCase()} will be permanently removed.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-btn" onClick={confirmDelete} className="rounded-none bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
